import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChannelBadge,
  type ChannelKind,
  ConfirmAction,
  EmptyState,
  Input,
  Label,
  ListPage,
  RefreshButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@skylabs-digital/backoffice-kit';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import { Plus, Search, Trash2, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { BudgetInput } from '../api/budgets.api';
import { budgetsApi } from '../api/budgets.api';
import { optOutsApi } from '../api/optouts.api';
import type { TenantPreferenceInput, UserPreferenceInput } from '../api/preferences.api';
import { tenantPreferencesApi, userPreferencesApi } from '../api/preferences.api';
import { PolicyChainSummary, PolicyExplainer } from '../components/preferences/PolicyExplainer';
import { type Scope, ScopePicker } from '../components/preferences/ScopePicker';
import { isManagedByCac, ManagedByCacBadge } from '../components/shared/ManagedByCac';
import { CHANNEL_OPTIONS } from '../constants/channel-colors';
import { MESSAGE_CATEGORY_OPTIONS } from '../constants/message-categories';
import { useSelectedApp } from '../context/SelectedApp';

/**
 * Sentinel for "no category filter". A Select cannot hold '' as a value, so the
 * empty filter needs a name of its own; it is translated back to '' before the
 * query is built.
 */
const ALL_CATEGORIES = '__all__';

const TABS = [
  { value: 'user' as const, label: 'User Preferences' },
  { value: 'tenant' as const, label: 'Tenant Preferences' },
  { value: 'optouts' as const, label: 'Opt-Outs' },
  { value: 'budgets' as const, label: 'Frequency Budgets' },
];

type TabValue = (typeof TABS)[number]['value'];

function SkeletonCells({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: cols }).map((_, i) => (
        <TableCell key={`s-${i}`}>
          <Skeleton className="h-4 w-20" />
        </TableCell>
      ))}
    </>
  );
}

function SearchBar({ children }: { children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid gap-4 md:grid-cols-[repeat(auto-fit,minmax(180px,1fr))_auto]">
          {children}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Tab 1: User Preferences ────────────────────────────────────────

function UserPreferencesResults({
  userId,
  tenantId,
  appId,
}: {
  userId: string;
  tenantId: string;
  appId: string;
}) {
  const { showSnackbar } = useSnackbar();
  const [showForm, setShowForm] = useState(false);
  const [formChannel, setFormChannel] = useState<string>(CHANNEL_OPTIONS[0]);
  const [formCategory, setFormCategory] = useState('');
  const [formEnabled, setFormEnabled] = useState(true);

  const scopedApi = userPreferencesApi
    .withParams({ userId })
    // `appId` va en TODAS las llamadas: sin el, resolveAppId no resuelve para
    // el superusuario y la API responde 403 en las escrituras y vacio en las
    // lecturas. Era la razon de fondo por la que esta pantalla no operaba nada.
    .withQuery(tenantId ? { appId, tenantId } : { appId });
  const { data, loading, error, refetch } = scopedApi.useList();
  const { mutate: createPreference } = scopedApi.useCreate();
  const { mutate: deletePreference } = scopedApi.useDelete();

  const handleAdd = async () => {
    if (!formCategory) return;
    try {
      const input: UserPreferenceInput = {
        channel: formChannel,
        messageCategory: formCategory,
        enabled: formEnabled,
      };
      await createPreference(input);
      await refetch();
      showSnackbar({ message: 'Preference saved', variant: 'success' });
      setShowForm(false);
      setFormCategory('');
    } catch {
      showSnackbar({ message: 'Failed to save preference', variant: 'error' });
    }
  };

  if (error) {
    return (
      <EmptyState
        title="Failed to load user preferences"
        action={<Button onClick={() => refetch()}>Retry</Button>}
        className="border-destructive/40 bg-destructive/5"
      />
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Preferences for {userId}</CardTitle>
        {/* El alcance decide a que fila se escribe, y las dos aplican al mismo
          usuario: sin decirlo, dos filas distintas se ven identicas. */}
        <CardDescription>
          {tenantId
            ? `Alcance: tenant ${tenantId}. Le gana a la preferencia de app.`
            : 'Alcance: toda la app. Aplica salvo que haya una preferencia del tenant.'}
        </CardDescription>
        <div className="flex items-center gap-2">
          <RefreshButton size="sm" onRefresh={refetch} isRefreshing={loading} />
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus /> Add
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {showForm ? (
          <div className="grid gap-3 border-b border-border bg-muted/20 p-4 md:grid-cols-[1fr_2fr_1fr_auto_auto]">
            <div className="space-y-1">
              <Label className="text-xs">Channel</Label>
              <Select value={formChannel} onValueChange={setFormChannel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message category</Label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {MESSAGE_CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Enabled</Label>
              <Select
                value={formEnabled ? 'true' : 'false'}
                onValueChange={(v) => setFormEnabled(v === 'true')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={handleAdd} disabled={!formCategory}>
                Save
              </Button>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Channel</TableHead>
              <TableHead>Message category</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`s-${i}`}>
                  <SkeletonCells cols={4} />
                </TableRow>
              ))
            ) : (data ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                  No preferences found.
                </TableCell>
              </TableRow>
            ) : (
              (data ?? []).map((pref) => (
                <TableRow key={pref.id}>
                  <TableCell>
                    <ChannelBadge channel={pref.channel as ChannelKind} />
                  </TableCell>
                  <TableCell>{pref.messageCategory}</TableCell>
                  <TableCell>
                    <StatusBadge tone={pref.enabled ? 'success' : 'danger'}>
                      {pref.enabled ? 'Yes' : 'No'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="text-right">
                    <ConfirmAction
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 /> Delete
                        </Button>
                      }
                      title="Delete this preference?"
                      destructive
                      confirmLabel="Delete"
                      onConfirm={async () => {
                        try {
                          await deletePreference(pref.id);
                          await refetch();
                          showSnackbar({ message: 'Preference deleted', variant: 'success' });
                        } catch {
                          showSnackbar({
                            message: 'Failed to delete preference',
                            variant: 'error',
                          });
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function UserPreferencesTab({ scope, initialUserId }: { scope: Scope; initialUserId?: string }) {
  const [userId, setUserId] = useState(initialUserId ?? '');
  // Con userId en la URL la busqueda ya viene hecha: el link del User
  // Inspector tiene que abrir el resultado, no un formulario a medio llenar.
  const [searchUserId, setSearchUserId] = useState(initialUserId ?? '');
  const tenantId = scope.tenantId.trim();

  // El usuario se sigue escribiendo a mano: es opcional en el modelo y no hay
  // padron de usuarios en noten. Pero ya no hace falta apretar Search para
  // poder agregar: alcanza con tener el id escrito.
  const handleSearch = () => {
    if (!userId.trim()) return;
    setSearchUserId(userId.trim());
  };

  return (
    <div className="space-y-6">
      <PolicyExplainer tab="user" />
      <SearchBar>
        <div className="space-y-2">
          <Label htmlFor="up-user">User ID *</Label>
          <Input
            id="up-user"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="user-123"
          />
          <p className="text-xs text-muted-foreground">
            Es una búsqueda por id: noten no tiene padrón de usuarios, así que hay que saber a quién
            mirar. Enter también busca.
          </p>
        </div>
        <div className="flex items-end">
          <Button onClick={handleSearch} disabled={!userId.trim() || !scope.appId}>
            <Search /> Buscar
          </Button>
        </div>
      </SearchBar>

      {!scope.appId ? (
        <EmptyState
          title="Elegí una app arriba"
          description="Las preferencias son por app: sin ella la API rechaza tanto la lectura como la escritura."
        />
      ) : searchUserId ? (
        // Sin tenant NO es un bloqueo: desde RFC 0003 Fase 1 una preferencia
        // puede ser de app (tenant_id NULL), que es el unico modo posible en
        // las apps sin tenants como resuelto. Exigirlo dejaba esa pantalla en
        // un callejon sin salida.
        <UserPreferencesResults userId={searchUserId} tenantId={tenantId} appId={scope.appId} />
      ) : (
        <EmptyState
          title="Buscá un usuario para ver o crear sus preferencias"
          description="Sin overrides, ese usuario recibe según los defaults del tenant."
        />
      )}
    </div>
  );
}

// ─── Tab 2: Tenant Preferences ──────────────────────────────────────

function TenantPreferencesResults({ tenantId, appId }: { tenantId: string; appId: string }) {
  const { showSnackbar } = useSnackbar();
  const [showForm, setShowForm] = useState(false);
  const [formChannel, setFormChannel] = useState<string>(CHANNEL_OPTIONS[0]);
  const [formCategory, setFormCategory] = useState('');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formBudget, setFormBudget] = useState('');

  const scopedApi = tenantPreferencesApi.withParams({ tenantId }).withQuery({ appId });
  const { data, loading, error, refetch } = scopedApi.useList();
  const { mutate: createPreference } = scopedApi.useCreate();
  const { mutate: deletePreference } = scopedApi.useDelete();

  const handleAdd = async () => {
    if (!formCategory) return;
    try {
      const input: TenantPreferenceInput = {
        channel: formChannel,
        messageCategory: formCategory,
        enabled: formEnabled,
        maxWeeklyBudget: formBudget ? Number(formBudget) : null,
      };
      await createPreference(input);
      await refetch();
      showSnackbar({ message: 'Tenant preference saved', variant: 'success' });
      setShowForm(false);
      setFormCategory('');
      setFormBudget('');
    } catch {
      showSnackbar({ message: 'Failed to save tenant preference', variant: 'error' });
    }
  };

  if (error) {
    return (
      <EmptyState
        title="Failed to load tenant preferences"
        action={<Button onClick={() => refetch()}>Retry</Button>}
        className="border-destructive/40 bg-destructive/5"
      />
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Preferences for tenant {tenantId}</CardTitle>
        <div className="flex items-center gap-2">
          <RefreshButton size="sm" onRefresh={refetch} isRefreshing={loading} />
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus /> Add
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {showForm ? (
          <div className="grid gap-3 border-b border-border bg-muted/20 p-4 md:grid-cols-[1fr_2fr_1fr_1fr_auto_auto]">
            <div className="space-y-1">
              <Label className="text-xs">Channel</Label>
              <Select value={formChannel} onValueChange={setFormChannel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message category</Label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {MESSAGE_CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Enabled</Label>
              <Select
                value={formEnabled ? 'true' : 'false'}
                onValueChange={(v) => setFormEnabled(v === 'true')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Max weekly budget</Label>
              <Input
                type="number"
                value={formBudget}
                onChange={(e) => setFormBudget(e.target.value)}
                placeholder="(optional)"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleAdd} disabled={!formCategory}>
                Save
              </Button>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Channel</TableHead>
              <TableHead>Message category</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead className="text-right">Max weekly budget</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`s-${i}`}>
                  <SkeletonCells cols={5} />
                </TableRow>
              ))
            ) : (data ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                  No tenant preferences found.
                </TableCell>
              </TableRow>
            ) : (
              (data ?? []).map((pref) => (
                <TableRow key={pref.id}>
                  <TableCell>
                    <ChannelBadge channel={pref.channel as ChannelKind} />
                  </TableCell>
                  <TableCell>{pref.messageCategory}</TableCell>
                  <TableCell>
                    <StatusBadge tone={pref.enabled ? 'success' : 'danger'}>
                      {pref.enabled ? 'Yes' : 'No'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {pref.maxWeeklyBudget ?? '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <ConfirmAction
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 /> Delete
                        </Button>
                      }
                      title="Delete this tenant preference?"
                      destructive
                      confirmLabel="Delete"
                      onConfirm={async () => {
                        try {
                          await deletePreference(pref.id);
                          await refetch();
                          showSnackbar({
                            message: 'Tenant preference deleted',
                            variant: 'success',
                          });
                        } catch {
                          showSnackbar({
                            message: 'Failed to delete tenant preference',
                            variant: 'error',
                          });
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function TenantPreferencesTab({ scope }: { scope: Scope }) {
  const tenantId = scope.tenantId.trim();
  const { hasNoTenants } = useSelectedApp();

  // Ya no hay buscador: el tenant se elige arriba y los resultados salen solos.
  // El buscador de antes existia solo porque no habia picker.
  return (
    <div className="space-y-6">
      <PolicyExplainer tab="tenant" />
      {!scope.appId ? (
        <EmptyState
          title="Elegí una app arriba"
          description="Las preferencias de tenant son por app."
        />
      ) : hasNoTenants ? (
        <EmptyState
          title="Esta app no maneja tenants"
          description="En idachu está configurada sin tenants, así que no hay preferencias de tenant que definir. Lo que aplica son las preferencias de usuario y los opt-outs."
        />
      ) : !tenantId ? (
        <EmptyState
          title="Elegí un tenant arriba"
          description="El picker lista los tenants de los que noten ya vio algo. Si el que buscás no está, se puede escribir el id a mano."
        />
      ) : (
        <TenantPreferencesResults tenantId={tenantId} appId={scope.appId} />
      )}
    </div>
  );
}

// ─── Tab 3: Opt-Outs ────────────────────────────────────────────────

function OptOutsResults({ userId, appId }: { userId: string; appId: string }) {
  const { showSnackbar } = useSnackbar();
  const [showForm, setShowForm] = useState(false);
  const [formChannel, setFormChannel] = useState<string>(CHANNEL_OPTIONS[0]);

  const scopedApi = optOutsApi.withParams({ userId }).withQuery({ appId });
  const { data, loading, error, refetch } = scopedApi.useList();
  const { mutate: createOptOut } = scopedApi.useCreate();
  const { mutate: deleteOptOut } = scopedApi.useDelete();

  if (error) {
    return (
      <EmptyState
        title="Failed to load opt-outs"
        action={<Button onClick={() => refetch()}>Retry</Button>}
        className="border-destructive/40 bg-destructive/5"
      />
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Opt-Outs for {userId}</CardTitle>
        <div className="flex items-center gap-2">
          <RefreshButton size="sm" onRefresh={refetch} isRefreshing={loading} />
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus /> Add opt-out
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {showForm ? (
          <div className="grid gap-3 border-b border-border bg-muted/20 p-4 md:grid-cols-[1fr_auto_auto]">
            <div className="space-y-1">
              <Label className="text-xs">Channel</Label>
              <Select value={formChannel} onValueChange={setFormChannel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={async () => {
                  try {
                    await createOptOut({ channel: formChannel });
                    await refetch();
                    showSnackbar({ message: 'Opt-out added', variant: 'success' });
                    setShowForm(false);
                  } catch {
                    showSnackbar({ message: 'Failed to add opt-out', variant: 'error' });
                  }
                }}
              >
                Add
              </Button>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Channel</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 2 }).map((_, i) => (
                <TableRow key={`s-${i}`}>
                  <SkeletonCells cols={4} />
                </TableRow>
              ))
            ) : (data ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                  No opt-outs found.
                </TableCell>
              </TableRow>
            ) : (
              (data ?? []).map((opt) => (
                <TableRow key={opt.channel}>
                  <TableCell>
                    <ChannelBadge channel={opt.channel as ChannelKind} />
                  </TableCell>
                  <TableCell>
                    {opt.reason ? (
                      <StatusBadge
                        tone="danger"
                        title="Automatic opt-out reported by the email provider"
                      >
                        {opt.reason}
                      </StatusBadge>
                    ) : (
                      <span className="text-xs text-muted-foreground">manual</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(opt.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <ConfirmAction
                      trigger={
                        <Button variant="outline" size="sm">
                          <UserPlus /> Opt-in
                        </Button>
                      }
                      title={`Restore ${opt.channel} opt-in?`}
                      description="The user will start receiving this channel again."
                      confirmLabel="Restore"
                      onConfirm={async () => {
                        try {
                          await deleteOptOut(opt.channel);
                          await refetch();
                          showSnackbar({ message: 'Opt-in restored', variant: 'success' });
                        } catch {
                          showSnackbar({
                            message: 'Failed to restore opt-in',
                            variant: 'error',
                          });
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function OptOutsTab({ scope, initialUserId }: { scope: Scope; initialUserId?: string }) {
  const [userId, setUserId] = useState(initialUserId ?? '');
  const [searchUserId, setSearchUserId] = useState(initialUserId ?? '');

  const handleSearch = () => {
    if (!userId.trim()) return;
    setSearchUserId(userId.trim());
  };

  return (
    <div className="space-y-6">
      <PolicyExplainer tab="optouts" />
      <SearchBar>
        <div className="space-y-2">
          <Label htmlFor="oo-user">User ID *</Label>
          <Input
            id="oo-user"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="user-123"
          />
          <p className="text-xs text-muted-foreground">
            Los opt-outs no dependen del tenant: son por app, usuario y canal.
          </p>
        </div>
        <div className="flex items-end">
          <Button onClick={handleSearch} disabled={!userId.trim() || !scope.appId}>
            <Search /> Buscar
          </Button>
        </div>
      </SearchBar>

      {!scope.appId ? (
        <EmptyState
          title="Elegí una app arriba"
          description="Los opt-outs son por app: el mismo usuario puede estar dado de baja en una y no en otra."
        />
      ) : searchUserId ? (
        <OptOutsResults userId={searchUserId} appId={scope.appId} />
      ) : (
        <EmptyState
          title="Buscá un usuario para ver sus bajas"
          description="Sin filas, ese usuario no se dio de baja de ningún canal."
        />
      )}
    </div>
  );
}

// ─── Tab 4: Frequency Budgets ───────────────────────────────────────

function FrequencyBudgetsTab({ scope }: { scope: Scope }) {
  const { showSnackbar } = useSnackbar();
  // El tenant sale del picker de arriba; el filtro propio que tenia esta
  // pestaña era el unico lugar de la pantalla donde se pegaba un id a mano.
  const filterTenantId = scope.tenantId.trim();
  const [filterUserId, setFilterUserId] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searched, setSearched] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [showForm, setShowForm] = useState(false);
  const [formTenantId, setFormTenantId] = useState('');
  const [formUserId, setFormUserId] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formLimit, setFormLimit] = useState('');

  // Esta pestaña ya listaba sin buscar (es una coleccion filtrable, a
  // diferencia de las otras tres que son lookups por id). Ahora ademas arranca
  // acotada al tenant elegido arriba.
  const scopedFilters: Record<string, string> = { appId: scope.appId };
  if (filterTenantId) scopedFilters.tenantId = filterTenantId;
  const { data, loading, error, refetch } = budgetsApi
    .withQuery(searched ? activeFilters : scopedFilters)
    .useList();
  const { mutate: createBudget } = budgetsApi.useCreate();

  const handleSearch = useCallback(() => {
    const filters: Record<string, string> = { appId: scope.appId };
    if (filterTenantId) filters.tenantId = filterTenantId;
    if (filterUserId) filters.userId = filterUserId;
    if (filterCategory) filters.messageCategory = filterCategory;
    setActiveFilters(filters);
    setSearched(true);
  }, [filterTenantId, filterUserId, filterCategory, scope.appId]);

  const handleCreate = async () => {
    if (!formCategory || !formLimit) return;
    try {
      const input: BudgetInput = {
        messageCategory: formCategory,
        weeklyLimit: Number(formLimit),
        tenantId: formTenantId || filterTenantId || null,
        userId: formUserId || null,
      };
      await createBudget(input);
      await refetch();
      showSnackbar({ message: 'Budget saved', variant: 'success' });
      setShowForm(false);
      setFormCategory('');
      setFormLimit('');
      setFormTenantId('');
      setFormUserId('');
    } catch {
      showSnackbar({ message: 'Failed to save budget', variant: 'error' });
    }
  };

  return (
    <div className="space-y-6">
      <SearchBar>
        <div className="space-y-2">
          <Label htmlFor="fb-user">User ID</Label>
          <Input
            id="fb-user"
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            placeholder="(optional)"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fb-cat">Message category</Label>
          <Select
            value={filterCategory || ALL_CATEGORIES}
            onValueChange={(v) => setFilterCategory(v === ALL_CATEGORIES ? '' : v)}
          >
            <SelectTrigger id="fb-cat">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CATEGORIES}>All categories</SelectItem>
              {MESSAGE_CATEGORY_OPTIONS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={handleSearch}>
            <Search /> Search
          </Button>
        </div>
      </SearchBar>

      {searched && error ? (
        <EmptyState
          title="Failed to load budgets"
          action={<Button onClick={() => refetch()}>Retry</Button>}
          className="border-destructive/40 bg-destructive/5"
        />
      ) : null}

      {searched && !error ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Frequency budgets</CardTitle>
            <div className="flex items-center gap-2">
              <RefreshButton size="sm" onRefresh={refetch} isRefreshing={loading} />
              <Button size="sm" onClick={() => setShowForm(true)}>
                <Plus /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {showForm ? (
              <div className="grid gap-3 border-b border-border bg-muted/20 p-4 md:grid-cols-[1fr_1fr_2fr_1fr_auto_auto]">
                <div className="space-y-1">
                  <Label className="text-xs">Tenant ID</Label>
                  <Input
                    value={formTenantId}
                    onChange={(e) => setFormTenantId(e.target.value)}
                    placeholder="(optional)"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">User ID</Label>
                  <Input
                    value={formUserId}
                    onChange={(e) => setFormUserId(e.target.value)}
                    placeholder="(optional)"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Message category *</Label>
                  <Select value={formCategory} onValueChange={setFormCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {MESSAGE_CATEGORY_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Weekly limit *</Label>
                  <Input
                    type="number"
                    value={formLimit}
                    onChange={(e) => setFormLimit(e.target.value)}
                    placeholder="100"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleCreate} disabled={!formCategory || !formLimit}>
                    Save
                  </Button>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User ID</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Weekly limit</TableHead>
                  <TableHead className="text-right">Current count</TableHead>
                  <TableHead>Tenant ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={`s-${i}`}>
                      <SkeletonCells cols={5} />
                    </TableRow>
                  ))
                ) : (data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      No budgets found.
                    </TableCell>
                  </TableRow>
                ) : (
                  (data ?? []).map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-mono text-xs">{b.userId ?? '—'}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-2">
                          {b.messageCategory}
                          {isManagedByCac(b) ? <ManagedByCacBadge /> : null}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{b.weeklyLimit}</TableCell>
                      <TableCell
                        className={
                          b.currentCount >= b.weeklyLimit
                            ? 'text-right font-semibold tabular-nums text-destructive'
                            : 'text-right tabular-nums text-foreground'
                        }
                      >
                        {b.currentCount}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{b.tenantId ?? '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────

export function PreferencesPage() {
  // El scope y la pestaña salen de la URL, asi que esta pantalla es
  // enlazable: el User Inspector y el detalle de App mandan para aca con el
  // contexto ya resuelto, y un link pegado en un ticket de soporte abre lo
  // mismo que vio quien lo escribio.
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get('tab') as TabValue | null;
  const [activeTab, setActiveTab] = useState<TabValue>(
    tabFromUrl && TABS.some((t) => t.value === tabFromUrl) ? tabFromUrl : 'user',
  );
  // El scope vive en la pagina y no en cada pestaña: cambiar de pestaña no
  // deberia hacerte volver a elegir la app y el tenant.
  // El appId lo manda el selector global del header. Un link con ?appId sigue
  // funcionando: al abrirlo selecciona esa app globalmente, en vez de dejar dos
  // verdades sobre "en que app estoy".
  const { appId: selectedAppId, setAppId } = useSelectedApp();
  const appIdFromUrl = searchParams.get('appId');
  useEffect(() => {
    if (appIdFromUrl && appIdFromUrl !== selectedAppId) setAppId(appIdFromUrl);
  }, [appIdFromUrl, selectedAppId, setAppId]);

  const [tenantId, setTenantId] = useState(searchParams.get('tenantId') ?? '');
  const scope: Scope = { appId: selectedAppId, tenantId };
  const setScope = (next: Scope) => setTenantId(next.tenantId);

  /** Refleja el estado en la URL para que el link se pueda copiar y compartir. */
  const syncUrl = (next: { scope?: Scope; tab?: TabValue }) => {
    const nextScope = next.scope ?? scope;
    const nextTab = next.tab ?? activeTab;
    const params = new URLSearchParams();
    if (nextScope.appId) params.set('appId', nextScope.appId);
    if (nextScope.tenantId.trim()) params.set('tenantId', nextScope.tenantId.trim());
    if (nextTab !== 'user') params.set('tab', nextTab);
    const userId = searchParams.get('userId');
    if (userId) params.set('userId', userId);
    setSearchParams(params, { replace: true });
  };

  return (
    <ListPage
      title="Preferences"
      description="Quién recibe qué: bajas, preferencias por categoría y topes de frecuencia."
      actions={
        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            setActiveTab(v as TabValue);
            syncUrl({ tab: v as TabValue });
          }}
        >
          <TabsList>
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      }
    >
      <PolicyChainSummary />

      <Card>
        <CardContent className="pt-6">
          <ScopePicker
            scope={scope}
            onChange={(next) => {
              setScope(next);
              syncUrl({ scope: next });
            }}
            showTenant={activeTab !== 'optouts'}
            tenantHint={
              activeTab === 'budgets'
                ? 'Opcional acá: sin tenant se ven los budgets de toda la app.'
                : undefined
            }
          />
        </CardContent>
      </Card>

      {activeTab === 'user' && (
        <UserPreferencesTab scope={scope} initialUserId={searchParams.get('userId') ?? undefined} />
      )}
      {activeTab === 'tenant' && <TenantPreferencesTab scope={scope} />}
      {activeTab === 'optouts' && (
        <OptOutsTab scope={scope} initialUserId={searchParams.get('userId') ?? undefined} />
      )}
      {activeTab === 'budgets' && <FrequencyBudgetsTab scope={scope} />}
    </ListPage>
  );
}
